"use client";

import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
} from "react";
import { usePathname } from "next/navigation";

export interface ScrollAnchorOptions {
  headerOffset?: number;
  updateUrl?: boolean;
  trackEvent?: (action: string, sectionId: string) => void;
}

interface ScrollAnchorContextType {
  activeSection: string;
  activePage: string;
  lastClickedSection: string; // Neue Funktion für Footer
  scrollToSection: (sectionId: string) => void;
  isActiveSection: (sectionId: string) => boolean;
  isActivePage: (pagePath: string) => boolean;
  isLastClickedSection: (sectionId: string) => boolean; // Neue Funktion für Footer
  getActiveClasses: (
    sectionId: string,
    baseClasses: string,
    activeClasses?: string
  ) => string;
  getPageActiveClasses: (
    pagePath: string,
    baseClasses: string,
    activeClasses?: string
  ) => string;
  getActiveAriaAttributes: (
    sectionId: string,
    baseLabel: string
  ) => {
    "aria-label": string;
    "aria-current"?: "page";
  };
}

const ScrollAnchorContext = createContext<ScrollAnchorContextType | undefined>(
  undefined
);

interface ScrollAnchorProviderProps {
  children: React.ReactNode;
  headerOffset?: number;
  updateUrl?: boolean;
}

export const ScrollAnchorProvider: React.FC<ScrollAnchorProviderProps> = ({
  children,
  headerOffset = 80,
  updateUrl = true,
}) => {
  const [activeSection, setActiveSection] = useState<string>("");
  const [lastClickedSection, setLastClickedSection] = useState<string>(""); // Neue State für Footer
  const pathname = usePathname();

  // Initialize active section from URL hash
  useEffect(() => {
    const updateActiveSection = () => {
      const hash = window.location.hash.replace("#", "");
      if (hash) {
        setActiveSection(hash);
      }
    };

    // Initial load - with delay to ensure DOM is ready
    const timer = setTimeout(() => {
      updateActiveSection();
    }, 100);

    // Listen for hash changes
    window.addEventListener("hashchange", updateActiveSection);

    return () => {
      clearTimeout(timer);
      window.removeEventListener("hashchange", updateActiveSection);
    };
  }, []);

  // Update active section when pathname changes (for page navigation)
  useEffect(() => {
    // Check if there's a hash in the URL after page change
    const hash = window.location.hash.replace("#", "");

    if (hash) {
      // If there's a hash, set it as active
      setActiveSection(hash);
    } else {
      // Clear active section when navigating to a new page without hash
      setActiveSection("");
    }
  }, [pathname]);

  // Simplified scroll-based active section detection
  useEffect(() => {
    // Only run on client side and after hydration
    if (typeof window === "undefined") return;

    let timeoutId: NodeJS.Timeout;
    
    const updateActiveSectionOnScroll = () => {
      try {
        // Dynamically detect sections based on current page
        const pathname = window.location.pathname;
        let sections: string[] = [];

        if (
          pathname === "/agentic-ai-tool/power-bi-mcp-server" ||
          pathname === "/services/power-bi-mcp-server" ||
          pathname === "/services/agentic-bi-tool" ||
          pathname === "/agentic-bi-tool"
        ) {
          sections = [
            "hero",
            "ki-data-governance",
            "target-audience",
            "next-level-governance",
            "dbi-expertise",
            "faq",
          ];
        } else if (pathname === "/") {
          sections = [
            "services-usp",
            "clients-trust",
            "team-mission", 
            "technologies",
            "europe-map",
            "testimonials",
          ];
        }

        if (sections.length === 0) return;

        let currentSection = "";
        const scrollPosition = window.scrollY + headerOffset;

        for (const sectionId of sections) {
          const element = document.getElementById(sectionId);
          if (element) {
            const elementTop = element.offsetTop;
            const elementBottom = elementTop + element.offsetHeight;

            if (scrollPosition >= elementTop && scrollPosition < elementBottom) {
              currentSection = sectionId;
              break;
            }
          }
        }

        if (currentSection !== activeSection && currentSection) {
          setActiveSection(currentSection);
          if (updateUrl && window.location.hash !== `#${currentSection}`) {
            window.history.replaceState(null, "", `#${currentSection}`);
          }
        }
      } catch (error) {
        console.warn("Scroll handling error:", error);
      }
    };

    // Debounced scroll handler
    const handleScroll = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(updateActiveSectionOnScroll, 16); // ~60fps
    };

    // Add scroll listener with error boundary
    try {
      window.addEventListener("scroll", handleScroll, { passive: true });
      
      // Initial check after a delay
      timeoutId = setTimeout(updateActiveSectionOnScroll, 200);
    } catch (error) {
      console.warn("Failed to add scroll listener:", error);
    }

    return () => {
      clearTimeout(timeoutId);
      try {
        window.removeEventListener("scroll", handleScroll);
      } catch (error) {
        console.warn("Failed to remove scroll listener:", error);
      }
    };
  }, [activeSection, headerOffset, updateUrl]);

  // Scroll to section function
  const scrollToSection = useCallback(
    (sectionId: string) => {
      try {
        const element = document.getElementById(sectionId);
        if (!element) return;

        // Update URL hash if enabled
        if (updateUrl) {
          window.history.pushState(null, "", `#${sectionId}`);
        }

        // Update active section immediately
        setActiveSection(sectionId);

        // Update last clicked section for footer logic
        setLastClickedSection(sectionId);

        // GA4 tracking with consent check
        if (typeof window !== "undefined") {
          console.log("🚀 [SCROLL] ===== TRACKING SCROLL EVENT =====");
          console.log("🎯 [SCROLL] Section ID:", sectionId);
          
          try {
            console.log("🔍 [SCROLL] Checking localStorage for cookie consent...");
            const savedConsent = localStorage.getItem("cookieConsent");
            
            if (savedConsent) {
              console.log("✅ [SCROLL] Cookie consent found in localStorage");
              const parsedConsent = JSON.parse(savedConsent);
              console.log("📊 [SCROLL] Parsed consent data:", parsedConsent);
              
              if (parsedConsent.analytics === true) {
                console.log("✅ [SCROLL] Analytics consent confirmed");
                const gtag = (window as any).gtag;
                
                if (typeof gtag === "function") {
                  console.log("✅ [SCROLL] gtag function is available");
                  console.log("📡 [SCROLL] Sending scroll event to GA4...");
                  console.log("📊 [SCROLL] GA4 scroll event (with consent):", sectionId);
                  
                  gtag("event", "scroll_to_section", {
                    event_category: "navigation",
                    event_label: sectionId,
                    value: 1,
                  });
                  
                  console.log("✅ [SCROLL] Scroll event successfully sent to GA4!");
                  console.log("🎉 [SCROLL] ===== SCROLL EVENT TRACKED =====");
                } else {
                  console.log("⚠️ [SCROLL] gtag function not available");
                }
              } else {
                console.log("⚠️ [SCROLL] GA4 scroll event blocked - no analytics consent");
                console.log("💡 [SCROLL] User has not consented to analytics tracking");
              }
            } else {
              console.log("⚠️ [SCROLL] GA4 scroll event blocked - no consent found");
              console.log("💡 [SCROLL] User needs to accept cookies first");
            }
          } catch (error) {
            console.error("❌ [SCROLL] Error checking consent for GA4 scroll event:", error);
            console.log("💥 [SCROLL] ===== SCROLL EVENT FAILED =====");
          }
        }

        // Calculate scroll position with header offset
        const elementPosition = element.getBoundingClientRect().top;
        const offsetPosition =
          elementPosition + window.pageYOffset - headerOffset;

        // Smooth scroll to element
        window.scrollTo({
          top: offsetPosition,
          behavior: "smooth",
        });
      } catch (error) {
        console.warn("Scroll to section error:", error);
      }
    },
    [headerOffset, updateUrl]
  );

  // Check if section is active
  const isActiveSection = useCallback(
    (sectionId: string): boolean => {
      return activeSection === sectionId;
    },
    [activeSection]
  );

  // Check if section was last clicked (for footer logic)
  const isLastClickedSection = useCallback(
    (sectionId: string): boolean => {
      return lastClickedSection === sectionId;
    },
    [lastClickedSection]
  );

  // Check if page is active
  const isActivePage = useCallback(
    (pagePath: string): boolean => {
      return pathname === pagePath;
    },
    [pathname]
  );

  // Get CSS classes for active section styling
  const getActiveClasses = useCallback(
    (
      sectionId: string,
      baseClasses: string,
      activeClasses: string = "text-aorange underline decoration-aorange decoration-2 underline-offset-4"
    ): string => {
      const isActive = isActiveSection(sectionId);
      return `${baseClasses} ${isActive ? activeClasses : ""}`;
    },
    [isActiveSection]
  );

  // Get CSS classes for active page styling
  const getPageActiveClasses = useCallback(
    (
      pagePath: string,
      baseClasses: string,
      activeClasses: string = "text-aorange underline decoration-aorange decoration-2 underline-offset-4"
    ): string => {
      // Check if current path matches exactly or is a sub-path
      const isActive = pathname === pagePath || pathname.startsWith(`${pagePath}/`);
      return `${baseClasses} ${isActive ? activeClasses : ""}`;
    },
    [pathname]
  );

  // Get ARIA attributes for active section
  const getActiveAriaAttributes = useCallback(
    (
      sectionId: string,
      baseLabel: string
    ): {
      "aria-label": string;
      "aria-current"?: "page";
    } => {
      const isActive = isActiveSection(sectionId);
      return {
        "aria-label": `${baseLabel}${isActive ? " (aktuell aktiv)" : ""}`,
        "aria-current": isActive ? "page" : undefined,
      };
    },
    [isActiveSection]
  );

  const value: ScrollAnchorContextType = {
    activeSection,
    activePage: pathname,
    lastClickedSection,
    scrollToSection,
    isActiveSection,
    isActivePage,
    isLastClickedSection,
    getActiveClasses,
    getPageActiveClasses,
    getActiveAriaAttributes,
  };

  return (
    <ScrollAnchorContext.Provider value={value}>
      {children}
    </ScrollAnchorContext.Provider>
  );
};

export const useScrollAnchor = (): ScrollAnchorContextType => {
  const context = useContext(ScrollAnchorContext);
  if (!context) {
    // Return a fallback implementation when provider is disabled
    return {
      activeSection: "",
      activePage: "",
      lastClickedSection: "",
      scrollToSection: () => {},
      isActiveSection: () => false,
      isActivePage: () => false,
      isLastClickedSection: () => false,
      getActiveClasses: (sectionId: string, baseClasses: string, activeClasses?: string) => baseClasses,
      getPageActiveClasses: (pagePath: string, baseClasses: string, activeClasses?: string) => baseClasses,
      getActiveAriaAttributes: (sectionId: string, baseLabel: string) => ({
        "aria-label": baseLabel,
      }),
    };
  }
  return context;
};
